/**
 * Qué instancia de facturación estás mirando.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 *
 * Hay DOS despliegues de esta app, en dos cuentas de Cloudflare distintas, y
 * **los dos logins se ven idénticos**: no hay forma de saber si estás por
 * entrar a la de verdad (1.320 facturas) o a la copia (0 facturas). Eso ya
 * costó 25 días de deploys que se fueron al lugar equivocado sin que nadie lo
 * notara.
 *
 * ── Por qué sale del HOST y no de una variable ──────────────────────────────
 *
 * Porque una variable se puede copiar mal, y justamente el incidente empezó
 * copiando mal un valor de configuración. El host al que le habla el front está
 * horneado en `API_BASE` al compilar: es el servidor que de verdad va a
 * responder, no lo que un archivo dice que debería responder. Si el rótulo y
 * los datos alguna vez discrepan, el rótulo es el que tiene razón.
 */

import { API_BASE } from './api';

/** Qué cuenta de Cloudflare hospeda cada worker. */
const INSTANCIAS = {
  'carlos-mdt-1057': {
    clave: 'carlos',
    cuenta: 'carlos.mdt.1057@gmail.com',
    etiqueta: 'Instancia original',
  },
  'betspupplier-fs': {
    clave: 'bet',
    cuenta: 'betspupplier.fs@gmail.com',
    etiqueta: 'Cuenta BET Supplier',
  },
} as const;

/**
 * Dónde vive la producción de verdad.
 *
 * Cambiado a la cuenta de BET el **2026-09-18**, cuando se completó la
 * migración: la base con las 1.438 facturas y el dominio
 * `appfacturacion.betsupplier.co` ya apuntan ahí. Con esta sola línea el cartel
 * de "esta instancia se está migrando" se encendió solo del lado de carlos, que
 * queda en pie un tiempo por las dudas.
 *
 * La bitácora completa está en `../../MIGRACION-A-CUENTA-BET.md`.
 */
const HOST_DE_PRODUCCION: keyof typeof INSTANCIAS = 'betspupplier-fs';

function hostDeLaApi(): string {
  try {
    return new URL(API_BASE, window.location.origin).hostname;
  } catch {
    return '';
  }
}

export interface Instancia {
  /** Host del worker que responde. */
  host: string;
  /** Cuenta de Cloudflare, si la reconocemos. */
  cuenta: string | null;
  etiqueta: string;
  /** `true` si este despliegue es el que se está usando de verdad. */
  esProduccion: boolean;
  /** `true` si es un despliegue conocido; `false` si apareció uno nuevo. */
  conocida: boolean;
}

export function instanciaActual(): Instancia {
  const host = hostDeLaApi();
  const entrada = Object.entries(INSTANCIAS).find(([k]) => host.includes(k));

  if (!entrada) {
    // Un host que no conocemos no se da por bueno en silencio: puede ser un
    // despliegue nuevo, o el front apuntando a donde no debe.
    return {
      host,
      cuenta: null,
      etiqueta: 'Servidor no reconocido',
      esProduccion: false,
      conocida: false,
    };
  }

  const [clave, info] = entrada;
  return {
    host,
    cuenta: info.cuenta,
    etiqueta: info.etiqueta,
    esProduccion: clave === HOST_DE_PRODUCCION,
    conocida: true,
  };
}
